import { z } from 'zod';
import { PointSchema } from './geojson';

/**
 * Live-tracking status derived from last_seen_at age (R.1b; basing tier #245).
 * 'based' = the transponder has been quiet past the stale window: the marker
 * persists at the LAST KNOWN position as ambient basing intel ("last seen here
 * / likely based nearby") — a claim about the final fix, never a verified base.
 */
export const AircraftTrackingStatusSchema = z.enum(['live', 'stale', 'based', 'undiscovered']);
export type AircraftTrackingStatus = z.infer<typeof AircraftTrackingStatusSchema>;

/** Quiet longer than this reads 'stale' (mirrors the worker sweep + API CASE). */
export const AIRCRAFT_STALE_AFTER_MINUTES = 30;
/** Quiet longer than this reads 'based' — the old hard-retirement cliff (#36). */
export const AIRCRAFT_BASED_AFTER_MINUTES = 120;
/**
 * Sanity cap: a quiet airframe older than this leaves the COP entirely, so a
 * permanently-retired tail doesn't haunt the map. 30 days — long enough for the
 * roster's discover-live military airframes (a LENTUS Chinook can sit silent
 * for weeks between sorties), short enough that "gone for a month" reads as
 * retired/re-registered rather than parked. Proposed 2026-06-10, pending
 * founder ratification (#245).
 */
export const AIRCRAFT_BASED_RETENTION_MINUTES = 30 * 24 * 60;

/**
 * Pure TS mirror of the SQL tier derivation in apps/api routes/aircraft.ts —
 * one boundary definition for client-side display logic and tests.
 */
export function trackingStatusFromAgeSeconds(ageSeconds: number | null): AircraftTrackingStatus {
  if (ageSeconds === null) return 'undiscovered';
  if (ageSeconds <= AIRCRAFT_STALE_AFTER_MINUTES * 60) return 'live';
  if (ageSeconds <= AIRCRAFT_BASED_AFTER_MINUTES * 60) return 'stale';
  return 'based';
}

export const AircraftFeaturePropertiesSchema = z.object({
  id: z.string().uuid(),
  tail_number: z.string(),
  /** Null for CAF aircraft whose military hex hasn't been captured live yet. */
  icao_hex: z.string().nullable(),
  aircraft_type: z.string(),
  aircraft_role: z.string().nullable(),
  operator_org: z.string().nullable(),
  /** The response_assets row this airframe belongs to (its base/operator). */
  operator_asset_id: z.string().uuid().nullable(),
  altitude_m: z.number().nullable(),
  speed_kt: z.number().nullable(),
  heading_deg: z.number().nullable(),
  last_seen_at: z.string().nullable(),
  tracking_status: AircraftTrackingStatusSchema,
  /** True when the hex was discovered from a live transmission (CAF watchlist). */
  hex_captured_from_live: z.boolean(),
});
export type AircraftFeatureProperties = z.infer<typeof AircraftFeaturePropertiesSchema>;

export const AircraftFeatureSchema = z.object({
  type: z.literal('Feature'),
  id: z.string().uuid(),
  geometry: PointSchema,
  properties: AircraftFeaturePropertiesSchema,
});
export type AircraftFeature = z.infer<typeof AircraftFeatureSchema>;

export const AircraftFeatureCollectionSchema = z.object({
  type: z.literal('FeatureCollection'),
  features: z.array(AircraftFeatureSchema),
});
export type AircraftFeatureCollection = z.infer<typeof AircraftFeatureCollectionSchema>;

export const AircraftDetailSchema = z.object({
  type: z.literal('Feature'),
  id: z.string().uuid(),
  /** Null when the aircraft has never reported a position (undiscovered). */
  geometry: PointSchema.nullable(),
  properties: AircraftFeaturePropertiesSchema.extend({
    base_name: z.string().nullable(),
    operator_asset_name: z.string().nullable(),
    engine_count: z.number().int().nullable(),
    status: z.string(),
    metadata: z.record(z.unknown()).nullable(),
  }),
});
export type AircraftDetail = z.infer<typeof AircraftDetailSchema>;
